import { useEffect } from "react";

/**
 * Applies a subtle parallax tilt + lift to the hero iPhone cluster
 * as the user scrolls past the hero.
 * Target element should have class `.iphone-cluster-stage` and
 * an inner `.iphone-cluster-float`.
 */
export default function useClusterScrollTilt(ref) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const stage = ref?.current;
    if (!stage) return;

    let rafId = 0;
    let targetX = 0, targetY = 0, targetT = 0;
    let curX = 0, curY = 0, curT = 0;

    const compute = () => {
      const rect = stage.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      // Progress: -1 (above viewport) → 0 (centered) → +1 (below viewport)
      const center = rect.top + rect.height / 2;
      const p = (center - vh / 2) / (vh / 2);
      const clamped = Math.max(-1.4, Math.min(1.4, p));
      // Tilt range: -6deg .. +6deg on X axis (looking up vs down)
      targetX = clamped * -4; // negative makes phones tilt "back" as you scroll down
      // Subtle Y rotation drift
      targetY = clamped * 1.5;
      // Lift / sink translate
      targetT = clamped * 18;
    };

    const tick = () => {
      curX += (targetX - curX) * 0.08;
      curY += (targetY - curY) * 0.08;
      curT += (targetT - curT) * 0.08;
      stage.style.setProperty("--scroll-tilt-x", `${curX.toFixed(2)}deg`);
      stage.style.setProperty("--scroll-tilt-y", `${curY.toFixed(2)}deg`);
      stage.style.setProperty("--scroll-translate-y", `${curT.toFixed(1)}px`);
      rafId = requestAnimationFrame(tick);
    };

    const onScroll = () => compute();
    compute();
    rafId = requestAnimationFrame(tick);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [ref]);
}
